// TeamAnswers — a question several teams answer at once (a deliverable's
// dossier, R18-A): each team answered on its own, pre-filled with the
// reading of that team, plus "Same for all". The answer is one
// SowAssignmentValue: the first team's, every other team's (and each one's
// own further lines) as its further lines, so Accept writes each team's.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraSelect, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
// The panel's choice for "Answer each team separately".
export const PER_TEAM = '__per_team__';
// Each team's pick starts at its reading; an unclear team starts unpicked.
export function initialTeamPicks(answers) {
    return answers.map((a)=>a.readingKey);
}
// The decided value, or '' until every team has a pick.
export function teamAnswersValueJson(answers, picks) {
    const lines = [];
    for (const [i, a] of answers.entries()){
        const option = a.options.find((o)=>o.key === picks[i]);
        if (!option) return '';
        let v;
        try {
            v = JSON.parse(option.valueJson);
        } catch  {
            return '';
        }
        const { each, ...own } = v;
        lines.push(own, ...each ?? []);
    }
    const [first, ...rest] = lines;
    if (!first) return '';
    return JSON.stringify(rest.length ? {
        ...first,
        each: rest
    } : first);
}
// "Answered per team: Quantum Records: Owes its own; AGEMO CoE: Not owed".
export function teamAnswersRationale(answers, picks, note) {
    const said = answers.map((a, i)=>`${a.teamName}: ${a.options.find((o)=>o.key === picks[i])?.label ?? ''}`).join('; ');
    const n = note.trim();
    return n ? `Answered per team: ${said}. ${n}` : `Answered per team: ${said}`;
}
// The options every team has, labelled as the first team's.
function sharedOptions(answers) {
    const [first, ...rest] = answers;
    if (!first) return [];
    return first.options.filter((o)=>rest.every((a)=>a.options.some((x)=>x.key === o.key))).map((o)=>({
            key: o.key,
            label: o.label
        }));
}
export function TeamAnswers({ answers, picks, onChange, interactive }) {
    const shared = sharedOptions(answers);
    return /*#__PURE__*/ _jsxs("div", {
        className: "mt-3 space-y-2",
        "data-testid": Testids.reviewItem.teamAnswers,
        "data-count": answers.length,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraText, {
                        as: "span",
                        size: "body-ui",
                        className: "w-48 shrink-0",
                        children: "Same for all"
                    }),
                    /*#__PURE__*/ _jsxs(SpecraSelect, {
                        "aria-label": "Same for all",
                        value: "",
                        disabled: !interactive,
                        onChange: (e)=>{
                            const key = e.target.value;
                            if (key) onChange(answers.map(()=>key));
                        },
                        "data-testid": Testids.reviewItem.teamAnswersSameForAll,
                        children: [
                            /*#__PURE__*/ _jsx("option", {
                                value: "",
                                children: "Set every team to…"
                            }),
                            shared.map((o)=>/*#__PURE__*/ _jsx("option", {
                                    value: o.key,
                                    children: o.label
                                }, o.key))
                        ]
                    })
                ]
            }),
            answers.map((a, i)=>/*#__PURE__*/ _jsxs("div", {
                    className: "flex items-center gap-2",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraText, {
                            as: "span",
                            size: "body-ui",
                            className: "w-48 shrink-0",
                            children: a.teamName
                        }),
                        /*#__PURE__*/ _jsxs(SpecraSelect, {
                            "aria-label": `Answer for ${a.teamName}`,
                            value: picks[i] ?? '',
                            invalid: !picks[i],
                            disabled: !interactive,
                            onChange: (e)=>onChange(picks.map((p, j)=>j === i ? e.target.value : p)),
                            "data-testid": Testids.reviewItem.teamAnswer,
                            "data-team": a.teamName,
                            "data-reading": a.readingKey,
                            children: [
                                /*#__PURE__*/ _jsx("option", {
                                    value: "",
                                    children: a.readingKey ? 'Pick…' : 'Unclear in the reading: pick…'
                                }),
                                a.options.map((o)=>/*#__PURE__*/ _jsx("option", {
                                        value: o.key,
                                        children: o.key === a.readingKey ? `${o.label} (as read)` : o.label
                                    }, o.key))
                            ]
                        })
                    ]
                }, `${a.workstreamId}-${a.teamName}`))
        ]
    });
}
